import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import DisciplineEmblem from "../../components/DisciplineEmblem";

/**
 * Page détail d'une discipline — POC Phase 2
 * URL : /plateforme/disciplines/:slug (ex: /plateforme/disciplines/kickboxing)
 * Cette page est ce que voit le visiteur quand il clique "EXPLORER →" sur la landing.
 * Elle transforme l'intérêt en intention d'inscription.
 */

const DISCIPLINES = {
  kickboxing: {
    chapter: "DISCIPLINE 01 — PILIER",
    title: "Kickboxing / Muay Thaï",
    tagline: "Le socle de tout combattant moderne — l'art des huit membres.",
    description:
      "La discipline-pilier de l'Institut. Frappe debout, gestion de la distance, pression et endurance, enrichies de l'héritage thaïlandais : coudes, genoux et clinch. Kickboxing et Muay Thaï forment un seul parcours de frappe complet — du langage commun des arts martiaux modernes jusqu'à la science du combat rapproché.",
    cycles: [
      { name: "Initiation", weeks: "4-12 sem.", focus: "Posture, garde, déplacements, jab, croix, teep" },
      { name: "Fondations", weeks: "12-24 sem.", focus: "Coups de pied bas/médians, combinaisons 2-3 frappes, esquive, genoux" },
      { name: "Structure", weeks: "24-52 sem.", focus: "Pression, gestion distance, contre-attaques, clinch, sparring contrôlé" },
      { name: "Stratégie", weeks: "52+ sem.", focus: "Lecture adversaire, feintes, coudes, plan de combat, performance ring" },
    ],
    techniques: [
      { name: "Jab + croix", level: "Fondamental" },
      { name: "Crochets (avant / arrière)", level: "Fondamental" },
      { name: "Uppercut", level: "Fondamental" },
      { name: "Teep (jab de pied)", level: "Fondamental" },
      { name: "Low kick (jambe avant / arrière)", level: "Intermédiaire" },
      { name: "Middle / round kick", level: "Intermédiaire" },
      { name: "High kick", level: "Avancé" },
      { name: "Genoux droits / circulaires (clinch)", level: "Intermédiaire" },
      { name: "Coude horizontal / vertical", level: "Intermédiaire" },
      { name: "Coude descendant (12-6)", level: "Avancé" },
      { name: "Clinch (cou-collier) + sweep", level: "Avancé" },
      { name: "Combinaisons 4+ frappes", level: "Avancé" },
      { name: "Spinning back kick / genou volant", level: "Élite" },
    ],
    benefits: [
      "Coordination + cardio explosif",
      "Endurance hors-norme et ténacité mentale",
      "Confiance dans la confrontation contrôlée",
      "Base technique réutilisable pour MMA, K-1 et compétition",
    ],
  },
  "jiu-jitsu-japonais": {
    chapter: "DISCIPLINE 02 — TRADITION",
    title: "Jiu-Jitsu Can Ryu",
    tagline: "La voie du contrôle stratégique.",
    description:
      "Tradition, contrôle, projections, immobilisations. Le Jiu-Jitsu Can Ryu enseigne à neutraliser sans détruire — l'art le plus complet de l'autodéfense.",
    cycles: [
      { name: "Ceinture blanche", weeks: "12-24 sem.", focus: "Roulades, chutes, frappes de base, premières immobilisations" },
      { name: "Ceinture jaune", weeks: "24-36 sem.", focus: "Projections fondamentales, défense contre saisies" },
      { name: "Ceinture orange", weeks: "36-48 sem.", focus: "Combinaisons projection-immobilisation, défense armée" },
      { name: "Ceinture verte → noire", weeks: "5+ ans", focus: "Tradition, kata, transmission" },
    ],
    techniques: [
      { name: "Ukemi (chutes contrôlées)", level: "Fondamental" },
      { name: "Atemi (frappes ciblées)", level: "Fondamental" },
      { name: "Saisies de poignet (kotegaeshi)", level: "Fondamental" },
      { name: "Projections (osoto-gari, ippon-seoi-nage)", level: "Intermédiaire" },
      { name: "Étranglements (shime-waza)", level: "Intermédiaire" },
      { name: "Clés articulaires (kansetsu-waza)", level: "Avancé" },
      { name: "Défense contre couteau", level: "Avancé" },
      { name: "Kata classiques", level: "Élite" },
    ],
    benefits: [
      "Discipline mentale et respect du lignage",
      "Autodéfense complète debout/sol",
      "Contrôle de l'agressivité",
      "Posture et présence renforcées",
    ],
  },
  "jiu-jitsu-bresilien": {
    chapter: "DISCIPLINE 03 — LABORATOIRE",
    title: "Jiu-Jitsu Brésilien",
    tagline: "L'art de la résolution de problèmes.",
    description:
      "Le sol comme laboratoire. Levier, patience, soumission. Le BJJ enseigne que la technique bat la force — et que chaque problème a une solution si on garde son calme.",
    cycles: [
      { name: "Petits Samouraïs (3-6 ans) — programme parents-enfants séparé", weeks: "Bloc 8 sem.", focus: "Mobilité, jeu, respect, coordination avec le parent" },
      { name: "Junior Blanc → Jaune (8-14 ans)", weeks: "12-52 sem.", focus: "Garde fermée, montée, échappées, soumissions douces" },
      { name: "Adulte Blanc → Bleu", weeks: "12-52 sem.", focus: "Garde, passage, contrôles, soumissions fondamentales" },
      { name: "Adulte Bleu → Mauve → Brune → Noire", weeks: "Plusieurs années", focus: "Stratégie, jeu personnel, transmission" },
    ],
    techniques: [
      { name: "Garde fermée (closed guard)", level: "Fondamental" },
      { name: "Montée (mount)", level: "Fondamental" },
      { name: "Side control + échappées", level: "Fondamental" },
      { name: "Étranglement croisé (cross collar choke)", level: "Fondamental" },
      { name: "Armbar de la garde", level: "Intermédiaire" },
      { name: "Triangle", level: "Intermédiaire" },
      { name: "Garde araignée (spider guard)", level: "Avancé" },
      { name: "Garde De La Riva", level: "Avancé" },
      { name: "Berimbolo", level: "Élite" },
      { name: "Transitions complexes", level: "Élite" },
    ],
    benefits: [
      "Résolution de problèmes en temps réel",
      "Humilité (le tatami enseigne)",
      "Endurance + souplesse",
      "Ceinture qui marque l'évolution sur des années",
    ],
  },
};


export default function DisciplineDetail() {
  const { slug } = useParams();
  const key = slug === "muay-thai" ? "kickboxing" : slug;
  const d = DISCIPLINES[key] || DISCIPLINES.kickboxing;

  useEffect(() => {
    document.title = `${d.title} — Institut Impact Élite`;
    const linkPreconn = document.createElement("link");
    linkPreconn.rel = "preconnect";
    linkPreconn.href = "https://fonts.googleapis.com";
    document.head.appendChild(linkPreconn);
    const linkFonts = document.createElement("link");
    linkFonts.rel = "stylesheet";
    linkFonts.href =
      "https://fonts.googleapis.com/css2?family=Playfair+Display:wght@400;700;900&family=Inter:wght@300;400;500;700&display=swap";
    document.head.appendChild(linkFonts);
    return () => {
      document.head.removeChild(linkPreconn);
      document.head.removeChild(linkFonts);
    };
  }, [d.title]);

  const baseStyle = {
    fontFamily: "Inter, system-ui, sans-serif",
    background: "#0d0d0d",
    color: "#f5f5f5",
    minHeight: "100vh",
  };

  const levelColor = (level) => {
    if (level === "Fondamental") return "#9CA3AF";
    if (level === "Intermédiaire") return "#C9A961";
    if (level === "Avancé") return "#C8102E";
    return "#fff";
  };

  return (
    <div style={baseStyle} data-testid="discipline-detail">
      <style>{`
        .editorial { font-family: 'Playfair Display', serif; }
        .label { letter-spacing: 0.4em; font-size: 11px; }
        .frame { border: 1px solid rgba(201, 169, 97, 0.25); }
        .divider-gold { background: linear-gradient(90deg, transparent, #C9A961 30%, #C9A961 70%, transparent); height: 1px; }
        .gold { color: #C9A961; }
        .red-c { color: #C8102E; }
        .bg-red-c { background: #C8102E; }
        .bg-gold { background: #C9A961; }
        .btn-primary { background: #C8102E; color: #fff; transition: 0.3s; }
        .btn-primary:hover { background: #fff; color: #C8102E; }
        .btn-secondary { color: #C9A961; border: 1px solid #C9A961; transition: 0.3s; background: transparent; }
        .btn-secondary:hover { background: #C9A961; color: #0d0d0d; }
        .pf-link { transition: color 0.2s; }
        .pf-link:hover { color: #C9A961; }
      `}</style>

      {/* === NAV === */}
      <nav className="px-10 py-6 flex items-center justify-between sticky top-0 z-50 backdrop-blur-md"
        style={{ background: "rgba(0,0,0,0.8)", borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
        <Link to="/plateforme" className="flex items-center gap-4" data-testid="nav-back">
          <img src="/logos/master_text_sm.webp" alt="Institut Impact Élite Martial" className="h-12 w-auto object-contain" data-testid="discipline-nav-master-logo" />
        </Link>
        <div className="hidden md:flex gap-6 text-sm items-center">
          <Link to="/plateforme" className="pf-link">← Toutes les disciplines</Link>
          <a href="#tarifs" className="btn-primary px-6 py-2 text-xs tracking-[0.3em] font-bold">DÉBUTER →</a>
        </div>
      </nav>

      {/* === HERO === */}
      <section className="px-10 py-20" data-testid="discipline-hero">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-start justify-between gap-6">
            <div className="label red-c mb-6">{d.chapter}</div>
            <DisciplineEmblem name={d.title} size={140} className="hidden sm:block -mt-4 drop-shadow-[0_6px_18px_rgba(0,0,0,0.7)]" testId="discipline-hero-emblem" />
          </div>
          <h1 className="editorial font-black text-6xl md:text-8xl mb-6 leading-[0.9]" data-testid="discipline-title">
            {d.title}
          </h1>
          <p className="editorial italic text-2xl md:text-3xl gold mb-10">{d.tagline}</p>
          <div className="divider-gold w-32 mb-10"></div>
          <p className="text-lg text-white/80 leading-relaxed max-w-3xl">{d.description}</p>
        </div>
      </section>

      {/* === LES CYCLES === */}
      <section className="px-10 py-20" style={{ background: "#161616" }} data-testid="cycles-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <div className="label red-c mb-4">— LE PARCOURS —</div>
            <h2 className="editorial font-black text-4xl md:text-5xl">Quatre cycles. Une progression mesurée.</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px" style={{ background: "rgba(201,169,97,0.2)" }}>
            {d.cycles.map((c, i) => (
              <div key={i} className="p-8" style={{ background: "#0d0d0d" }} data-testid={`cycle-${i}`}>
                <div className="editorial font-black text-6xl gold mb-4" style={{ opacity: 0.6 }}>0{i + 1}</div>
                <div className="editorial font-bold text-2xl mb-2">{c.name}</div>
                <div className="label gold mb-4">{c.weeks}</div>
                <div className="text-sm text-white/70 leading-relaxed">{c.focus}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === TECHNIQUES === */}
      <section className="px-10 py-20" data-testid="techniques-section">
        <div className="max-w-6xl mx-auto">
          <div className="mb-12">
            <div className="label red-c mb-4">— LE RÉPERTOIRE —</div>
            <h2 className="editorial font-black text-4xl md:text-5xl">Le langage technique.</h2>
            <p className="text-white/60 mt-4 max-w-2xl">Chaque technique est documentée, démontrée, validée par paliers. Tu sais ce que tu apprends. Tu sais où tu en es.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-px" style={{ background: "rgba(201,169,97,0.15)" }}>
            {d.techniques.map((t, i) => (
              <div key={i} className="p-6 flex items-center justify-between" style={{ background: "#0d0d0d" }} data-testid={`technique-${i}`}>
                <div className="flex items-center gap-4">
                  <div className="editorial font-black text-2xl text-white/30">{String(i + 1).padStart(2, "0")}</div>
                  <div className="text-base">{t.name}</div>
                </div>
                <div className="label" style={{ color: levelColor(t.level) }}>{t.level}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === BÉNÉFICES === */}
      <section className="px-10 py-20" style={{ background: "#161616" }} data-testid="benefits-section">
        <div className="max-w-5xl mx-auto">
          <div className="mb-12">
            <div className="label red-c mb-4">— LES BÉNÉFICES —</div>
            <h2 className="editorial font-black text-4xl md:text-5xl">Ce que tu construis vraiment.</h2>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            {d.benefits.map((b, i) => (
              <div key={i} className="frame p-8" style={{ background: "#0d0d0d" }} data-testid={`benefit-${i}`}>
                <div className="editorial font-black text-4xl gold mb-3">0{i + 1}</div>
                <div className="text-white/85 leading-relaxed">{b}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === CTA TARIFS === */}
      <section className="px-10 py-20" id="tarifs" data-testid="cta-section">
        <div className="max-w-4xl mx-auto text-center">
          <div className="label red-c mb-6">— PROCHAINE ÉTAPE —</div>
          <h2 className="editorial font-black text-4xl md:text-6xl mb-6">Tu es prêt à commencer.</h2>
          <p className="text-white/70 max-w-2xl mx-auto mb-12">
            Choisis le cycle qui correspond à ton engagement. Démarre par le PCO si tu veux découvrir, ou par le PCN si tu veux progresser sérieusement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/plateforme#tarifs" className="btn-primary px-10 py-4 font-bold tracking-[0.2em] text-xs">
              VOIR LES CYCLES & TARIFS →
            </Link>
            <Link to="/plateforme" className="btn-secondary px-10 py-4 font-bold tracking-[0.2em] text-xs">
              EXPLORER LES AUTRES DISCIPLINES
            </Link>
          </div>
        </div>
      </section>

      {/* === FOOTER === */}
      <footer className="px-10 py-12" style={{ borderTop: "1px solid rgba(201,169,97,0.2)" }}>
        <div className="max-w-6xl mx-auto">
          <div className="text-center">
            <div className="editorial font-black text-2xl mb-2">Institut Impact Élite</div>
            <div className="text-sm text-white/60 mb-6">Val-d'Or, Québec, Canada · sdm.rdjp@gmail.com</div>
            <div className="divider-gold w-32 mx-auto mb-4"></div>
            <div className="text-xs text-white/40 tracking-widest">© 2026 — POC PHASE 2 · DÉTAIL DISCIPLINE</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
